// Pure waitlist parse / retry / summary logic for the Adora POS proxy.
//
// This module has NO Deno- or Supabase-specific imports on purpose: the edge
// function (index.ts) and the Vitest suite in src/test/ both import it, so the
// spec's Critical upstream behaviour is unit-tested against fixtures without
// reaching the live POS. Keep it dependency-free.
//
// Spec references: 1.3 (double parse, empty data), 1.6 (Azure Front Door
// HTML-under-200, retry-and-diagnose), 3.1 (summary + timezone normalisation).

export const POS_BASE_URL = "https://pos.adorapos.net";
export const UPSTREAM_ATTEMPTS = 3;
export const UPSTREAM_RETRY_DELAY_MS = 500;
/** Cap on one upstream attempt. A hung POS must not hold the edge function
 *  open until the platform kills it: that reaches the client as a non-JSON
 *  platform error instead of the JSON-on-every-path contract (spec 3.4). */
export const UPSTREAM_TIMEOUT_MS = 10_000;

/**
 * The `Access-Control-Allow-Headers` this function must advertise to the browser.
 *
 * The SPA's request is preflighted (it carries custom headers), so EVERY header
 * the browser attaches has to be listed here or Chrome rejects the preflight and
 * never sends the GET — the "TypeError: Failed to fetch before the request is
 * sent" bug seen live. Beyond the client's own `authorization` + `apikey` (auth
 * is two layers), Sentry's browser tracing transparently adds `baggage` and
 * `sentry-trace` (older SDKs: `x-sentry-trace`) to outgoing fetches. Tracing on
 * this call is wanted, so the server must accept the headers rather than the
 * client dropping them.
 *
 * Single source of truth: index.ts advertises exactly this list, and the Vitest
 * suite asserts it covers every header the client actually transmits, so the two
 * can never drift back apart.
 */
export const WAITLIST_CORS_ALLOW_HEADERS = [
  "authorization",
  "x-client-info",
  "apikey",
  "content-type",
  "baggage",
  "sentry-trace",
  "x-sentry-trace",
] as const;

/** The header list as the single comma-separated string the CORS response needs. */
export const WAITLIST_CORS_ALLOW_HEADERS_VALUE = WAITLIST_CORS_ALLOW_HEADERS.join(", ");

/**
 * Decodes the claims of a bearer token WITHOUT verifying its signature: both
 * waitlist-orders and waitlist-snapshot run under `verify_jwt = true`, so the
 * gateway has already verified the signature and this only reads who the
 * caller is. Never use it behind a `verify_jwt = false` function. Returns null
 * for anything unreadable. Dependency-free (atob + TextDecoder are global in
 * Deno and Node) so it lives here as the single copy shared by both functions.
 */
export function decodeClaims(authHeader: string | null): Record<string, unknown> | null {
  const token = (authHeader ?? "").replace(/^Bearer\s+/i, "").trim();
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return claims && typeof claims === "object" && !Array.isArray(claims) ? claims : null;
  } catch {
    return null;
  }
}

/**
 * `verify_jwt = true` alone only proves the caller presented SOME valid project
 * JWT — and the publishable anon key is exactly that, shipped in the browser
 * bundle. A real Clerk->Supabase user token carries role `authenticated` and a
 * non-empty `sub`; the anon key carries role `anon` and no subject. The
 * service_role key is also rejected: it is a server credential (the snapshot
 * scheduler sends it to clear the gateway), never an end user, so it can only
 * ever take waitlist-snapshot's secret-authenticated scheduled path.
 */
export function isSignedInUser(claims: Record<string, unknown> | null): boolean {
  if (!claims) return false;
  const role = typeof claims.role === "string" ? claims.role : "";
  if (role === "anon" || role === "service_role") return false;
  return typeof claims.sub === "string" && claims.sub.trim() !== "";
}

// The OUTER-envelope parse failure is tagged with this message so the retry loop
// can tell it from an inner-`data` parse failure (which means the envelope WAS
// valid JSON, i.e. the upstream answered coherently and a retry would not help).
const NON_JSON_BODY_MESSAGE = "POS returned a non-JSON body";

/** Minimal shape we need from a fetch Response, so tests can supply fakes. */
export interface UpstreamResponse {
  status: number;
  headers: { get(name: string): string | null };
  text(): Promise<string>;
}

export interface OrderRecord {
  OrderNo?: number | string;
  CustomerName?: string;
  OrderStatus?: string;
  PromiseTime?: string;
  CookEndTime?: string;
  [key: string]: unknown;
}

export interface WaitlistSummary {
  order_count: number;
  earliest_promise_time: string | null;
  latest_promise_time: string | null;
}

/**
 * The POS answered, but not with the JSON we expected. Carries the HTTP context
 * needed to tell a transient Azure edge blip from a structural problem, per
 * spec 1.6: a bare parser message ("Expecting value: line 1 column 1") is
 * undiagnosable after the fact.
 */
export class UpstreamFormatError extends Error {
  readonly status: number;
  readonly contentType: string;
  readonly bodyLength: number;
  readonly bodySnippet: string;

  constructor(
    message: string,
    ctx: { status: number; contentType: string | null; body: string; detail: string },
  ) {
    const body = (ctx.body || "").slice(0, 300);
    const looksLikePage = /<html/i.test(body) || /login/i.test(body);
    const hint = looksLikePage
      ? " This looks like an HTML page rather than API output (an Azure Front Door edge error, not real waitlist data); usually a transient upstream blip, retry in a moment."
      : " This is usually a transient upstream blip; retry in a moment.";
    super(
      `${message} (HTTP ${ctx.status}, content-type=${JSON.stringify(ctx.contentType)}, ` +
        `${ctx.body.length} bytes): ${ctx.detail}. Body starts: ${JSON.stringify(body)}.${hint}`,
    );
    this.name = "UpstreamFormatError";
    this.status = ctx.status;
    this.contentType = ctx.contentType ?? "";
    this.bodyLength = ctx.body.length;
    this.bodySnippet = body;
  }
}

/**
 * The POS never answered at all: the connection failed or the per-attempt
 * timeout aborted it. Like a non-JSON body this is a transient condition, so it
 * is retried; the final one still reaches the client as the JSON 502 contract.
 */
export class UpstreamUnreachableError extends Error {
  constructor(detail: string) {
    super(
      `POS request did not complete: ${detail}. ` +
        `Each attempt is capped at ${UPSTREAM_TIMEOUT_MS}ms; this is usually a transient upstream blip, retry in a moment.`,
    );
    this.name = "UpstreamUnreachableError";
  }
}

/**
 * The POS answered with an HTTP error status. Spec 5 needs the status itself in
 * the message: "API reported Success=false" with no status is undiagnosable
 * when the real cause was a 403 or a 500. 5xx/429 are treated as blips and
 * retried; every other error status is a definite answer and propagates.
 */
export class UpstreamHttpError extends Error {
  readonly status: number;
  readonly retryable: boolean;

  constructor(status: number, contentType: string | null, body: string) {
    const snippet = (body || "").slice(0, 300);
    const retryable = status === 429 || status >= 500;
    super(
      `HTTP error from POS API: ${status} (content-type=${JSON.stringify(contentType)}, ` +
        `${(body || "").length} bytes). Body starts: ${JSON.stringify(snippet)}.` +
        (retryable
          ? " This is usually a transient upstream blip; retry in a moment."
          : " The POS refused the request; check the store id and the endpoint."),
    );
    this.name = "UpstreamHttpError";
    this.status = status;
    this.retryable = retryable;
  }
}

/** Raised when the POS returns {"Success": false, ...}: surface it verbatim. */
export class UpstreamRejectedError extends Error {
  constructor(envelope: unknown) {
    super(`API reported Success=false: ${JSON.stringify(envelope)}`);
    this.name = "UpstreamRejectedError";
  }
}

/**
 * Parses one POS response body into an order array. Does the DOUBLE parse the
 * contract requires: the outer envelope is JSON, and its `data` field is itself
 * a JSON-encoded string that must be parsed again (spec 1.3). An empty or
 * whitespace-only `data` means "no waitlist orders", returned as [] rather than
 * a parse error. Throws UpstreamFormatError on non-JSON (so the caller can
 * retry) and UpstreamRejectedError on Success=false.
 */
export function parseWaitlistBody(
  status: number,
  contentType: string | null,
  body: string,
): OrderRecord[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (e) {
    // Not JSON at all: almost always an Azure Front Door HTML error page served
    // under a 200. The status check never catches it; the parse is where it
    // surfaces. Retryable.
    throw new UpstreamFormatError(NON_JSON_BODY_MESSAGE, {
      status,
      contentType,
      body,
      detail: e instanceof Error ? e.message : String(e),
    });
  }

  // `null`, a number or a bare string all parse cleanly but carry no envelope.
  // Same class as a non-JSON body (and so retryable) rather than a raw
  // TypeError escaping the typed error contract.
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new UpstreamFormatError(NON_JSON_BODY_MESSAGE, {
      status,
      contentType,
      body,
      detail: `expected a JSON object envelope, got ${parsed === null ? "null" : typeof parsed}`,
    });
  }
  const outer = parsed as Record<string, unknown>;

  if (!outer.Success) {
    throw new UpstreamRejectedError(outer);
  }

  const raw = typeof outer.data === "string" ? outer.data : "";
  if (!raw.trim()) return [];

  let inner: unknown;
  try {
    inner = JSON.parse(raw);
  } catch (e) {
    throw new UpstreamFormatError("POS 'data' field was not valid JSON", {
      status,
      contentType,
      body: raw,
      detail: e instanceof Error ? e.message : String(e),
    });
  }
  // Contract says data is an array of order objects; anything else is coerced to
  // empty rather than crashing the response.
  return Array.isArray(inner) ? (inner as OrderRecord[]) : [];
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface FetchWaitlistOptions {
  fetchImpl: (url: string) => Promise<UpstreamResponse>;
  attempts?: number;
  delayMs?: number;
  sleepImpl?: (ms: number) => Promise<void>;
  onAttemptError?: (attempt: number, error: Error) => void;
}

/**
 * Fetches a store's waitlist with the spec 1.6 retry-and-diagnose policy: on a
 * non-JSON body, retry up to `attempts` times with `delayMs` between tries
 * (these blips clear within a second), then raise the rich UpstreamFormatError.
 * A request that never produces a response at all (network failure, or the
 * caller's per-attempt timeout aborting it) follows the same retry policy and
 * finally raises UpstreamUnreachableError.
 * An HTTP 5xx or 429 from the POS follows the same retry policy and finally
 * raises UpstreamHttpError. A Success=false envelope, an unparseable inner
 * `data`, or any other error status is NOT retried (it is a definite answer,
 * not a blip) and propagates immediately.
 *
 * The store id in the URL path is the ONLY selector. The request is bare: no
 * Cookie (would pin every response to one store, overriding the URL), no
 * Content-Length, no custom headers. `fetchImpl` must add none of those.
 */
export async function fetchWaitlistOrders(
  storeId: string,
  opts: FetchWaitlistOptions,
): Promise<OrderRecord[]> {
  const attempts = opts.attempts ?? UPSTREAM_ATTEMPTS;
  const delayMs = opts.delayMs ?? UPSTREAM_RETRY_DELAY_MS;
  const doSleep = opts.sleepImpl ?? sleep;
  const url = `${POS_BASE_URL}/Waitlist/V2_Waitlist_Orders_Get/${encodeURIComponent(storeId)}`;

  let lastError: Error | null = null;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    let resp: UpstreamResponse;
    let contentType: string | null;
    let body: string;
    try {
      resp = await opts.fetchImpl(url);
      contentType = resp.headers.get("content-type");
      body = await resp.text();
    } catch (e) {
      // No usable response: a network failure or the attempt timeout firing.
      // Same policy as an HTML-under-200 blip, so it retries within the same
      // attempt budget rather than failing the whole request on one hiccup.
      const err = new UpstreamUnreachableError(e instanceof Error ? `${e.name}: ${e.message}` : String(e));
      lastError = err;
      opts.onAttemptError?.(attempt, err);
      if (attempt < attempts) {
        await doSleep(delayMs);
        continue;
      }
      throw err;
    }
    try {
      // The status is checked before the body: a 403 or a 500 whose body still
      // parses as JSON must not be reported as a content problem (spec 5).
      if (resp.status < 200 || resp.status >= 300) {
        throw new UpstreamHttpError(resp.status, contentType, body);
      }
      return parseWaitlistBody(resp.status, contentType, body);
    } catch (e) {
      // Only a transient condition is worth retrying: a non-JSON OUTER body, or
      // a 5xx/429 from the POS. A definite answer (a 4xx refusal, Success=false,
      // or an inner `data` string that is present but not JSON) will not change
      // on retry, so let it propagate.
      if (e instanceof UpstreamHttpError && e.retryable) {
        lastError = e;
        opts.onAttemptError?.(attempt, e);
        if (attempt < attempts) {
          await doSleep(delayMs);
          continue;
        }
        throw e;
      }
      if (e instanceof UpstreamFormatError && isRetryable(e)) {
        lastError = e;
        opts.onAttemptError?.(attempt, e);
        if (attempt < attempts) {
          await doSleep(delayMs);
          continue;
        }
        throw e;
      }
      throw e;
    }
  }
  // Unreachable, but keeps the contract explicit.
  throw lastError ?? new Error("Waitlist fetch failed with no error recorded");
}

function isRetryable(e: UpstreamFormatError): boolean {
  return e.message.startsWith(NON_JSON_BODY_MESSAGE);
}

/**
 * Parses an ISO 8601 timestamp to epoch millis, or null if unparseable. A
 * value WITHOUT an offset (offset-naive) is interpreted as the runtime's local
 * time, matching the reference's "treat a naive stamp as the store's local
 * time" (spec 3.1). In JS, `new Date("2026-08-28T13:45:00")` already parses a
 * bare date-time as local, so comparing naive and offset-aware stamps by epoch
 * never throws the way Python's aware/naive comparison does.
 */
export function toEpoch(value: unknown): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Adora numbers the day's orders starting at 100, so the count of orders taken
 * so far is (highest OrderNo seen) - 100 — NOT the number of rows returned
 * (the waitlist only lists orders still pending, not every order of the day).
 * OrderNo may arrive as a number or a numeric string; non-numeric/missing
 * values are ignored. Never negative: a highest OrderNo at or below 100 (or no
 * valid OrderNo at all) means 0 orders so far.
 */
export function countOrdersFromOrderNo(orders: OrderRecord[]): number {
  const list = Array.isArray(orders) ? orders : [];
  let highest = -Infinity;
  for (const o of list) {
    if (!o || typeof o !== "object" || Array.isArray(o)) continue;
    const raw = (o as OrderRecord).OrderNo;
    const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
    if (Number.isFinite(n) && n > highest) highest = n;
  }
  return Number.isFinite(highest) ? Math.max(0, highest - 100) : 0;
}

/**
 * A day's order_count is a running HIGH-WATER MARK per (store, business_date):
 * the waitlist empties as orders are fulfilled, so a later capture (notably
 * the 23:50 nightly one) would otherwise read 0 for a busy day. A fresh count
 * never lowers a stored one; with no prior row the fresh count (including 0)
 * stands. A null prior (only failed captures so far) counts as no prior.
 */
export function highWaterMarkCount(
  storedOrderCount: number | null | undefined,
  freshOrderCount: number,
): number {
  const fresh = Number.isFinite(freshOrderCount) ? Math.max(0, freshOrderCount) : 0;
  if (typeof storedOrderCount !== "number" || !Number.isFinite(storedOrderCount)) return fresh;
  return Math.max(storedOrderCount, fresh);
}

/**
 * Computes the {order_count, earliest_promise_time, latest_promise_time}
 * summary (spec 3.1). order_count follows the Adora OrderNo numbering rule
 * (countOrdersFromOrderNo), not the length of the returned list, since the
 * waitlist only lists orders still pending. The min/max skip non-object
 * entries and unparseable / missing PromiseTime values rather than failing the
 * whole request. A payload mixing offset-naive and offset-aware timestamps
 * computes cleanly. The earliest/latest are returned as the original strings
 * the POS sent, so the client renders exactly that instant.
 */
export function computeSummary(orders: OrderRecord[]): WaitlistSummary {
  const list = Array.isArray(orders) ? orders : [];
  let earliest: { epoch: number; raw: string } | null = null;
  let latest: { epoch: number; raw: string } | null = null;

  for (const o of list) {
    if (!o || typeof o !== "object" || Array.isArray(o)) continue;
    const raw = (o as OrderRecord).PromiseTime;
    if (typeof raw !== "string") continue;
    const epoch = toEpoch(raw);
    if (epoch === null) continue;
    if (!earliest || epoch < earliest.epoch) earliest = { epoch, raw };
    if (!latest || epoch > latest.epoch) latest = { epoch, raw };
  }

  return {
    order_count: countOrdersFromOrderNo(list),
    earliest_promise_time: earliest ? earliest.raw : null,
    latest_promise_time: latest ? latest.raw : null,
  };
}
